// src/CustomButtons.js
import api from "../api/axios";

/** file picker */
function pickFile({ accept = "image/*,video/*" } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.multiple = false;
    input.onchange = () =>
      resolve(input.files && input.files[0] ? input.files[0] : null);
    input.click();
  });
}

async function upload_document_rtodo_ridderiq_onprem(ctx) {
  const {
    companyId,
    connectionId,
    moduleId,
    recordId,
    userEmail,
    userName,
    reloadActiveSubtable,
    startLoading,
    stopLoading,
    popup,
  } = ctx;

  try {
    const file = await pickFile({ accept: "image/*,video/*" });
    if (!file) return;

    const isAllowedType = /^image\/|^video\//i.test(file.type);
    if (!isAllowedType)
      return popup?.flashError?.("Only images and videos are allowed");

    const MAX_BYTES = 25 * 1024 * 1024;
    if (file.size > MAX_BYTES)
      return popup?.flashError?.("File is too large (max 25 MB)");

    startLoading?.("Uploading...");

    const fd = new FormData();
    fd.append("actionName", "upload_document_rtodo_ridderiq_onprem");
    fd.append("companyId", String(companyId));
    fd.append("connection_id", String(connectionId));
    fd.append("email", userEmail || "");
    fd.append("userName", (userName || "").trim());
    fd.append("moduleId", String(moduleId));
    fd.append("recordId", String(recordId));
    fd.append("file", file, file.name);

    const resp = await api.post("/api/custombuttons", fd);
    if (resp?.data?.success) {
      popup?.flashSuccess?.("Upload successful");
      reloadActiveSubtable?.();
    } else {
      popup?.flashError?.(resp?.data?.error || "Upload failed");
    }
  } catch (e) {
    popup?.flashError?.(`Upload failed: ${e?.response?.data?.error || e.message}`);
  } finally {
    stopLoading?.();
  }
}

/** open cart with config (headerButton) */
export async function show_shopping_cart(ctx) {
  ctx?.openCart?.(ctx?.button || null);
}

/** close cart */
export async function hide_shopping_cart(ctx) {
  ctx?.closeCart?.();
}

/** add row to cart */
export async function add_to_shopping_cart(ctx) {
  const {
    rowData,
    moduleId,
    recordId: recordIdFromCtx,
    primaryKeyField,
    addToCart,
    fieldsToShow,
    fields_to_show,
    popup,
  } = ctx || {};

  if (!moduleId || !addToCart) return;

  const recordId =
    recordIdFromCtx ??
    (rowData && primaryKeyField ? rowData[primaryKeyField] : null);

  if (recordId == null) {
    popup?.flashError?.("No recordId found for add_to_shopping_cart");
    return;
  }

  const rawFields = fieldsToShow ?? fields_to_show;
  const fields = Array.isArray(rawFields)
    ? rawFields
    : String(rawFields || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  const data = {};
  for (const f of fields) data[f] = rowData?.[f];

  try {
    await addToCart({ moduleId, recordId, fieldsToShow: fields, data });
    popup?.flashSuccess?.("Added to shopping cart");
  } catch (e) {
    popup?.flashError?.(
      e?.response?.data?.error || e?.message || "Failed to add item to cart"
    );
  }
}

/**
 * ✅ ERP-specific headerinfo loader (Ridder iQ On-Prem)
 * IMPORTANT: this function MUST NOT call React state setters.
 * It returns { success, values, options } so the caller can set state.
 */
export async function shopping_cart_headerinfo_ridderiq_onprem(ctx) {
  const {
    companyId,
    connectionId,
    userEmail,
    popup,
    cartConfig,
    orderHeader,
  } = ctx || {};

  if (!companyId || !connectionId) {
    return { success: false, error: "Missing companyId/connectionId" };
  }
  if (!userEmail) {
    return { success: false, error: "Missing userEmail." };
  }

  try {
    const res = await api.post("/api/shopping_cart/headerinfo", {
      companyId,
      connection_id: connectionId,
      email: userEmail,
      orderFieldsConfig: cartConfig?.OrderFields || {},
      currentValues: orderHeader?.values || {},
    });

    if (!res?.data?.success) {
      const msg = res?.data?.error || "Failed to load order header info.";
      popup?.flashError?.(msg);
      return { success: false, error: msg };
    }

    return {
      success: true,
      values: res.data?.values || {},
      options: res.data?.options || {},
    };
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || "Headerinfo error";
    popup?.flashError?.(msg);
    return { success: false, error: msg };
  }
}

/**
 * Ridder iQ On-Prem purchase: maakt R_ORDER + R_SALESORDERDETAILITEM via backend gateway call.
 */
export async function purchase_from_shopping_cart_ridderiq_on_prem_purchaseorder(ctx) {
  const {
    companyId,
    userEmail,
    popup,
    startLoading,
    stopLoading,
    refreshCart,
    cartItems,
    orderHeaderValues,
    cartConfig,
    onPurchaseSuccess, // ✅ callback from modal
  } = ctx || {};

  const cfg = cartConfig || {};
  const connectionId =
    cfg?.shopping_cart_headerInfo_connection_id || cfg?.connection_id || cfg?.connectionId;

  try {
    startLoading?.("One moment please, processing purchase order...");

    if (!companyId) return popup?.flashError?.("Missing companyId.");
    if (!userEmail) return popup?.flashError?.("Missing userEmail.");
    if (!connectionId) return popup?.flashError?.("Missing connection id.");
    if (!Array.isArray(cartItems) || cartItems.length === 0)
      return popup?.flashError?.("Your cart is empty.");

    const res = await api.post("/api/shopping_cart/purchase_ridderiq_onprem", {
      companyId,
      connection_id: connectionId,
      email: userEmail,

      orderFieldsConfig: cfg?.OrderFields || {},
      orderHeaderValues: orderHeaderValues || {},
      priceField: cfg?.priceField || null,

      orderLinesItemFieldsConfig: cfg?.OrderLines_Item_Fields || {},
    });

    if (!res?.data?.success) {
      popup?.flashError?.(res?.data?.error || "Purchase failed");
      return;
    }

    const orderNumber = res.data?.orderNumber;
    const allOk = !!res.data?.allOk;
    const failedCount = Number(res.data?.failedCount || 0);

    if (!allOk) {
      popup?.flashError?.(
        orderNumber
          ? `Order ${orderNumber} created, but ${failedCount} lines failed. Failed lines remain in cart.`
          : `Order created, but ${failedCount} lines failed. Failed lines remain in cart.`
      );
      return;
    }

    // ✅ show success dialog in modal (modal will close cart on OK)
    onPurchaseSuccess?.(res.data);

    await refreshCart?.();
  } catch (e) {
    popup?.flashError?.(e?.response?.data?.error || e?.message || "Purchase failed");
  } finally {
    stopLoading?.();
  }
}


/**
 * Klantspecifieke Ridder iQ purchase-flow.
 * Klant: Evenution (ELC Lighting, Green-GO)
 * De backend maakt één R_ORDER per unieke
 * C_CATALOG.FK_ORDERCATEGORY.
 */
export async function purchase_from_shopping_cart_ridderiq_onprem_grouped_by_catalog(
  ctx
) {
  const {
    companyId,
    userEmail,
    popup,
    startLoading,
    stopLoading,
    refreshCart,
    cartItems,
    orderHeaderValues,
    cartConfig,
    onPurchaseSuccess,
  } = ctx || {};

  const cfg = cartConfig || {};

  const connectionId =
    cfg?.shopping_cart_headerInfo_connection_id ||
    cfg?.connection_id ||
    cfg?.connectionId;

  const groupingConfig = cfg?.catalogOrderGrouping || {};

  const defaultOrderCategoryId = Number(
    groupingConfig?.defaultOrderCategoryId
  );

  try {
    startLoading?.(
      "One moment please, creating purchase orders..."
    );

    if (!companyId) {
      popup?.flashError?.("Missing companyId.");
      return;
    }

    if (!userEmail) {
      popup?.flashError?.("Missing userEmail.");
      return;
    }

    if (!connectionId) {
      popup?.flashError?.("Missing connection id.");
      return;
    }

    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      popup?.flashError?.("Your cart is empty.");
      return;
    }

    if (
      !Number.isInteger(defaultOrderCategoryId) ||
      defaultOrderCategoryId <= 0
    ) {
      popup?.flashError?.(
        "Missing or invalid catalogOrderGrouping.defaultOrderCategoryId."
      );
      return;
    }

    const res = await api.post(
      "/api/shopping_cart/purchase_ridderiq_onprem_grouped_by_catalog",
      {
        companyId,
        connection_id: connectionId,
        email: userEmail,

        orderFieldsConfig: cfg?.OrderFields || {},
        orderHeaderValues: orderHeaderValues || {},
        priceField: cfg?.priceField || null,

        orderLinesItemFieldsConfig:
          cfg?.OrderLines_Item_Fields || {},

        orderExtensionConfig: cfg?.extension_tables?.order_header || {},

        groupingConfig,
      }
    );

    if (!res?.data?.success) {
      popup?.flashError?.(
        res?.data?.error || "Purchase failed."
      );
      return;
    }

    /*
     * De backend verwijdert alleen succesvol ingevoegde
     * orderregels. Daarom ook bij gedeeltelijk succes
     * de winkelwagen opnieuw ophalen.
     */
    await refreshCart?.();

    if (!res.data?.allOk) {
      popup?.flashError?.(
        res.data?.message ||
          `${Number(
            res.data?.failedCount || 0
          )} cart line(s) failed. Failed lines remain in the cart.`
      );
      return;
    }

    const orderNumberText =
      Array.isArray(res.data?.orderNumbers) &&
      res.data.orderNumbers.length > 0
        ? res.data.orderNumbers.join(", ")
        : res.data?.orderNumber ?? null;

    onPurchaseSuccess?.({
      ...res.data,
      orderNumber: orderNumberText,
    });
  } catch (e) {
    popup?.flashError?.(
      e?.response?.data?.error ||
        e?.message ||
        "Purchase failed."
    );
  } finally {
    stopLoading?.();
  }
}


export async function purchase_from_shopping_cart_send_email(ctx) {
  const {
    companyId,
    userEmail,
    userName,
    popup,
    startLoading,
    stopLoading,
    refreshCart,
    cartItems,
    cartConfig,
    onPurchaseSuccess,
  } = ctx || {};

  try {
    startLoading?.("One moment please, sending purchase request email...");

    if (!companyId) return popup?.flashError?.("Missing companyId.");
    if (!userEmail) return popup?.flashError?.("Missing userEmail.");
    if (!Array.isArray(cartItems) || cartItems.length === 0)
      return popup?.flashError?.("Your cart is empty.");

    const emailCfg = cartConfig?.email_configuration || {};
    if (!emailCfg?.sendto) {
      return popup?.flashError?.("Missing email_configuration.sendto in JSON definition.");
    }

    const res = await api.post("/api/shopping_cart/send_email", {
      companyId,
      email: userEmail,
      userName: userName || "",
      emailConfiguration: emailCfg,
    });

    if (!res?.data?.success) {
      popup?.flashError?.(res?.data?.error || "Failed to send email.");
      return;
    }

    const msg = res.data?.message || "Email sent successfully. Cart cleared.";

    // ✅ make UI reflect backend delete immediately
    await refreshCart?.();

    if (onPurchaseSuccess) {
      onPurchaseSuccess({
        success: true,
        title: "Purchase request sent",
        orderNumber: null,
        message: msg,
        closeCartOnOk: true,
      });
    } else {
      popup?.flashSuccess?.(msg);
    }
  } catch (e) {
    popup?.flashError?.(e?.response?.data?.error || e?.message || "Failed to send email");
  } finally {
    stopLoading?.();
  }
}


async function chat_function(ctx) {
  ctx?.openChat?.();
}


export async function show_qr_code(ctx) {
  const {
    companyId,
    moduleId,
    recordId,
    rowData,
    button,
    popup,
    startLoading,
    stopLoading,
    openQrCode,
  } = ctx || {};

  if (!companyId) {
    return popup?.flashError?.(
      "Missing companyId."
    );
  }

  if (!moduleId) {
    return popup?.flashError?.(
      "Missing moduleId."
    );
  }

  if (
    recordId === undefined ||
    recordId === null ||
    String(recordId).trim() === ""
  ) {
    return popup?.flashError?.(
      "Missing recordId."
    );
  }

  if (!openQrCode) {
    return popup?.flashError?.(
      "QR popup is not available."
    );
  }

  try {
    startLoading?.(
      "Generating QR code..."
    );

    const response =
      await api.post(
        "/api/qr/create_link",
        {
          companyId,
          moduleId,
          recordId,
          frontend_base_url:
            window.location.origin,
        }
      );

    if (!response?.data?.success) {
      return popup?.flashError?.(
        response?.data?.error ||
          "Unable to generate QR code."
      );
    }

    const cfg =
      button || {};

    const recordField =
      cfg.qrRecordField;

    const descriptionField =
      cfg.qrDescriptionField;

    const recordValue =
      recordField
        ? rowData?.[recordField]
        : recordId;

    const description =
      descriptionField
        ? rowData?.[
            descriptionField
          ]
        : "";

    openQrCode({
      url: response.data.url,

      title:
        cfg.qrTitle ||
        "QR Code",

      recordLabel:
        cfg.qrRecordLabel ||
        "Record",

      recordValue:
        recordValue ??
        recordId,

      description:
        description || "",

      instruction:
        cfg.qrInstruction ||
        "Scan to open this record.",
    });

  } catch (e) {
    popup?.flashError?.(
      e?.response?.data?.error ||
        e?.message ||
        "Unable to generate QR code."
    );

  } finally {
    stopLoading?.();
  }
}


export const actionRegistry = {
  upload_document_rtodo_ridderiq_onprem,
  chat_function,

  show_qr_code,

  show_shopping_cart,
  hide_shopping_cart,
  add_to_shopping_cart,

  shopping_cart_headerinfo_ridderiq_onprem,
  purchase_from_shopping_cart_ridderiq_on_prem_purchaseorder,
  purchase_from_shopping_cart_ridderiq_onprem_grouped_by_catalog, // Custom maatwerk Evenution
  purchase_from_shopping_cart_send_email,
};

export function executeAction(actionName, ctx) {
  console.log("executeAction:", actionName);

  const fn = actionRegistry[actionName];
  if (!fn) {
    const msg = `No handler registered for "${actionName}".`;
    if (ctx?.popup?.flashError) {
      ctx.popup.flashError(msg);
      return;
    }
    alert(msg);
    return;
  }
  return fn(ctx);
}
